/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import { AttributeState, ShareCardDefinitionBackpackCategory, ShareCardDefinition } from './ShareCardTemplateModel';
import GroupContentModel, { GroupContentDataInterface } from './GroupContentModel';
import FileModel from './FileModel';
import NoteModel from './NoteModel';
import { ContactModelType } from './ContactModel';
import HealthEntryCategoryNames, { AllergySubType } from '../constants/HealthEntryCategoryNames';
import ShareCardIncludedTypeNames from '../constants/ShareCardIncludedTypeNames';
import { getLocalText } from '../utilities/localizedUtil';
import { RGBA } from '../utilities/types';
import { ShareCardColor } from '../apiServices/ListsService';
import { healthEntryCategories } from './healthEntryCategories';
import { HealthEntry } from './HealthEntry';
import { IRegisteredJewelry } from './RegisteredJewelry';

export interface ShareCard {
  id: number;
  user_id: number;
  profile_id: number;
  definition_id: number;
  title: string;
  comment: string;
  color_id: number;
  include_name: boolean;
  include_date_of_birth: boolean;
  include_gender: boolean;
  include_birth_sex: boolean;
  include_blood_type: boolean;
  include_race: boolean;
  include_photo: boolean;
  include_residence: boolean;
  included_health_entry_values: {
    health_entry_id: number;
    included_values: ShareCardIncludedTypeNames;
  }[];
  included_trackables: {
    trackable_id: number;
    included_values: ShareCardIncludedTypeNames;
  }[];
  created_at: string;
  updated_at: string;
  url_accessible: boolean;
  access_code: string;
  last_accessed_at: string;
  included_files: number[];
  included_notes: number[];
  included_contacts: number[];
  included_group_content: number[];
  share_card_jewelry: IRegisteredJewelry[];
}

const defaultShareCardData: ShareCard = {
  id: -1,
  user_id: -1,
  profile_id: -1,
  definition_id: null,
  title: null,
  comment: '',
  color_id: 1,
  include_name: false,
  include_date_of_birth: false,
  include_gender: false,
  include_birth_sex: false,
  include_blood_type: false,
  include_race: false,
  include_photo: false,
  include_residence: false,
  included_health_entry_values: [],
  included_trackables: [],
  created_at: '',
  updated_at: '',
  url_accessible: false,
  access_code: '',
  last_accessed_at: '',
  included_files: [],
  included_notes: [],
  included_contacts: [],
  included_group_content: [],
  share_card_jewelry: [],
};

export interface HealthEntries {
  all: HealthEntry[];
  newer: HealthEntry[];
  older: HealthEntry[];
}

export type HealthEntriesPerCategory = { [category: string]: HealthEntries };

export interface ShareCardItem {
  toggle();
  isIncluded();
  isExcluded();
  isRequired();
}

export class ShareCardItem {
  constructor(private getIncluded: () => boolean,
              private setIncluded: (i: boolean) => void,
              private getAttribute: () => AttributeState,
              private triggerChange: () => void) {
  }

  toggle() {
    if (this.isExcluded() || this.isRequired()) { return; }
    this.setIncluded(!this.getIncluded());
    this.triggerChange();
  }

  isIncluded() {
    return this.getIncluded();
  }

  isExcluded() {
    return this.getAttribute() === AttributeState.excluded;
  }

  isRequired() {
    return this.getAttribute() === AttributeState.required;
  }

  init() {
    if (this.isExcluded()) {
      this.setIncluded(false);
    }
    if (this.isRequired() || this.getAttribute() === AttributeState.defaulted) {
      this.setIncluded(true);
    }
  }
}

export class ShareCardItemGroup {
  constructor(public readonly items: ShareCardItem[], private attribute: AttributeState) {
  }

  countTotal() {
    return this.items.filter(item => !item.isExcluded()).length;
  }

  countIncluded() {
    return this.items.filter(item => item.isIncluded()).length;
  }

  toggleAll() {
    const nextValue = this.countTotal() !== this.countIncluded();

    this.items
      .forEach(item => (nextValue !== item.isIncluded()) && item.toggle());
  }

  init() {
    this.items.forEach(item => item.init());
  }

  isExcluded() {
    return this.attribute === AttributeState.excluded;
  }

  isRequired() {
    return this.attribute === AttributeState.required;
  }
}

type ShareCardChangeListener = (card: ShareCardModel) => void;

/**
 * Provide unified interfaces to the share card attributes, given that some attributes are defined
 * in share card template and some in the share card itself
 */
class ShareCardModel {

  static includesFile(scData, fileId): boolean {
    return scData.included_files.includes(fileId);
  }

  static ofData(
    data: Partial<ShareCard>,
    definitions: ShareCardDefinition[],
    params: {
      groupContents?: GroupContentDataInterface[],
      onChange?: ShareCardChangeListener,
    } = {},
  ): ShareCardModel {
    const definition = definitions.find(def => def.id === data.definition_id);
    if (!definition) return null;
    const { groupContents, onChange } = Object.assign({
      groupContents: [],
      onChange: () => {},
    }, params);

    const groupContentModels = (groupContents || [])
      .map((content: GroupContentDataInterface) => new GroupContentModel(content));

    return new ShareCardModel(data, definition, groupContentModels, onChange);
  }

  static isIncludedHealthEntry(scData: ShareCard, healthEntryId) {
    const heMatch = scData.included_health_entry_values.find((he) => he.health_entry_id === healthEntryId);
    return heMatch ? true : false;
  }

  static isIncludedTrackable(scData: ShareCard, trackableId) {
    const includedTrackables = scData.included_trackables || [];
    const heMatch = includedTrackables.find((he) => he.trackable_id === trackableId);
    return heMatch ? true : false;
  }

  static isIncludedFile(scData: ShareCard, fileId: number) {
    return scData.included_files.find((fId) => fId === fileId) ? true : false;
  }

  static isIncludedGroupContent(scData: ShareCard, contentId: number) {
    return scData.included_group_content.find((gId) => gId === contentId) ? true : false;
  }

  static getIsIncluded(scData: ShareCard, healthEntryId) {
    const heMatches = scData.included_health_entry_values.filter((he) => he.health_entry_id === healthEntryId);

    if (heMatches.length === 0) {
      return null;
    } else if (heMatches.length === 1) {
      return heMatches[0];
    }

    return heMatches.find(he => he.included_values === ShareCardIncludedTypeNames.all);
  }

  static getIsTrackableIncluded(scData: ShareCard, trackableId) {
    const includedTrackables = scData.included_trackables || [];
    const matches = includedTrackables.filter((t) => t.trackable_id === trackableId);

    if (matches.length === 0) {
      return null;
    } else if (matches.length === 1) {
      return matches[0];
    }

    return matches.find(he => he.included_values === ShareCardIncludedTypeNames.all);
  }

  static getIsExcluded(definition: ShareCardDefinition, healthEntryCategory, healthEntrySubType) {
    return ShareCardModel.getDefinitionInclusion(definition, healthEntryCategory, healthEntrySubType) === AttributeState.excluded;
  }

  static getIsRequired(definition: ShareCardDefinition, healthEntryCategory, healthEntrySubType) {
    return ShareCardModel.getDefinitionInclusion(definition, healthEntryCategory, healthEntrySubType) === AttributeState.required;
  }

  static toggleIncluded(scData: ShareCard, definition: ShareCardDefinition, healthEntry) {
    let newValues = [];
    const healthEntryId = healthEntry.id;
    const category = healthEntry.category;
    const inclusionValue = ShareCardModel.getDefinitionInclusionValue(definition, category);

    if (ShareCardModel.isIncludedHealthEntry(scData, healthEntryId)) {
      newValues = scData.included_health_entry_values.filter((he) => he.health_entry_id !== healthEntryId);
    } else {
      newValues = [...scData.included_health_entry_values];
      newValues.push({
        health_entry_id: healthEntryId,
        included_values: inclusionValue,
      });
    }

    return {
      ...scData,
      included_health_entry_values: newValues,
    };
  }

  static toggleTrackableIncluded(scData: ShareCard, definition: ShareCardDefinition, trackableId) {
    const includedTrackables = scData.included_trackables || [];
    let newValues = [];
    const inclusionValue = ShareCardModel.getDefinitionInclusionValue(definition, HealthEntryCategoryNames.condition);

    if (ShareCardModel.getIsTrackableIncluded(scData, trackableId)) {
      newValues = includedTrackables.filter((t) => t.trackable_id !== trackableId);
    } else {
      newValues = [...includedTrackables];
      newValues.push({
        trackable_id: trackableId,
        included_values: inclusionValue,
      });
    }

    return {
      ...scData,
      included_trackables: newValues,
    };
  }

  static toggleIncludedFile(scData: ShareCard, fileId) {
    let newValues = [];

    if (ShareCardModel.isIncludedFile(scData, fileId)) {
      newValues = scData.included_files.filter((fId) => fId !== fileId);
    } else {
      newValues = [...scData.included_files, fileId];
    }

    return {
      ...scData,
      included_files: newValues,
    };
  }

  static toggleIncludedType(scData: ShareCard, healthEntryId) {
    const heMatch = ShareCardModel.getIsIncluded(scData, healthEntryId);
    const newValues = scData.included_health_entry_values.filter((he) => he.health_entry_id !== healthEntryId);
    newValues.push({
      health_entry_id: healthEntryId,
      included_values: heMatch.included_values === ShareCardIncludedTypeNames.all ? ShareCardIncludedTypeNames.latest : ShareCardIncludedTypeNames.all,
    });

    return {
      ...scData,
      included_health_entry_values: newValues,
    };
  }

  static toggleTrackableIncludedType(scData: ShareCard, trackableId) {
    const includedTrackables = scData.included_trackables || [];
    const tMatch = ShareCardModel.getIsTrackableIncluded(scData, trackableId);
    const newValues = includedTrackables.filter((t) => t.trackable_id !== trackableId);
    newValues.push({
      trackable_id: trackableId,
      included_values: tMatch.included_values === ShareCardIncludedTypeNames.all ? ShareCardIncludedTypeNames.latest : ShareCardIncludedTypeNames.all,
    });

    return {
      ...scData,
      included_trackables: newValues,
    };
  }

  static getDefinitionInclusionValue(definition: ShareCardDefinition, category: HealthEntryCategoryNames): ShareCardIncludedTypeNames {
    switch (category) {
      case HealthEntryCategoryNames.lab:
        return definition.health_entry.labs_occurrence;
      case HealthEntryCategoryNames.measurement:
        return definition.health_entry.measurements_occurrence;
      case HealthEntryCategoryNames.geneticResult:
        return definition.health_entry.genetic_results_occurrence;
      case HealthEntryCategoryNames.allergy:
      case HealthEntryCategoryNames.condition:
      case HealthEntryCategoryNames.medication:
      case HealthEntryCategoryNames.otherTreatment:
      case HealthEntryCategoryNames.procedure:
      case HealthEntryCategoryNames.vaccination:
      default:
        return ShareCardIncludedTypeNames.all;
    }
  }

  static getDefaultInclusionValue(backpackCategory: ShareCardDefinitionBackpackCategory, definition: ShareCardDefinition) {
    return definition.backpack[backpackCategory] || ShareCardIncludedTypeNames.all;
  }

  static getDefinitionInclusion(definition: ShareCardDefinition, category: HealthEntryCategoryNames, subType: string): AttributeState {
    switch (category) {
      case HealthEntryCategoryNames.allergy:
        if (subType === AllergySubType.medication) {
          return definition.health_entry.allergies_medication;
        } else if (subType === AllergySubType.food) {
          return definition.health_entry.allergies_food;
        } else {
          return definition.health_entry.allergies_other;
        }
      case HealthEntryCategoryNames.lab:
        return definition.health_entry.labs;
      case HealthEntryCategoryNames.measurement:
        return definition.health_entry.measurements;
      case HealthEntryCategoryNames.geneticResult:
        return definition.health_entry.genetic_results;
      case HealthEntryCategoryNames.condition:
        return definition.health_entry.conditions;
      case HealthEntryCategoryNames.medication:
        return definition.health_entry.medications;
      case HealthEntryCategoryNames.otherTreatment:
        return definition.health_entry.treatments;
      case HealthEntryCategoryNames.procedure:
        return definition.health_entry.procedures;
      case HealthEntryCategoryNames.vaccination:
        return definition.health_entry.vaccinations;
      default:
        return null;
    }
  }

  static getTitle(scData, definition, languageCode) {
    // empty string check instead of just !! to allow setting title to default value while editing
    if (!definition || !scData) { return ''; }

    return !definition.title_editable ||
    (scData.title !== '' && !scData.title)
      ? getLocalText(definition.title, languageCode)
      : scData.title;
  }

  static isEmergencyShareCard(definition) {
    return definition.uid === 'backpack.emergency';
  }

  static toggleURL(scData, value) {
    return {
      ...scData,
      url_accessible: value,
    };
  }

  static isURLAccessible(scData) {
    return scData.url_accessible;
  }

  static toggleHealthEntryIncludedAll(scData: ShareCard, definition: ShareCardDefinition, items): ShareCard {
    if (items.length === 0) {
      return {
        ...scData,
        included_health_entry_values: [],
      };
    }

    const ids = scData.included_health_entry_values.map(h => h.health_entry_id);

    const heIds = items.map(he => he.id);
    const category = items[0].category;
    const includedHEIds = ids.filter(id => heIds.includes(id));
    const allHEsToggled = includedHEIds.length === heIds.length;
    const otherIncludedItems = scData.included_health_entry_values.filter((he) => !heIds.includes(he.health_entry_id));

    if (allHEsToggled) {
      return {
        ...scData,
        included_health_entry_values: otherIncludedItems,
      };
    } else {
      const defaultIncludedValue = ShareCardModel.getDefinitionInclusionValue(definition, category);

      const newItems = heIds.map(id => {
        return {
          health_entry_id: id,
          included_values: defaultIncludedValue,
        };
      });

      return {
        ...scData,
        included_health_entry_values: [
          ...otherIncludedItems,
          ...newItems,
        ],
      };
    }
  }

  static toggleTrackablesIncludedAll(scData: ShareCard, definition: ShareCardDefinition, items): ShareCard {
    const defaultInclusion = ShareCardModel.getDefaultInclusionValue(ShareCardDefinitionBackpackCategory.trackablesOccurrence, definition);
    const newItems = scData.included_trackables.length === items.length
      ? []
      : items.map(i => {
        return {
          trackable_id: i.id,
          included_values: defaultInclusion,
        };
      });

    return {
      ...scData,
      included_trackables: newItems,
    };
  }

  static setGroupContent(scData: ShareCard, contentId, isIncluded) {
    return {
      ...scData,
      included_group_content: isIncluded
      ? [...scData.included_group_content, contentId]
      : scData.included_group_content.filter(d => d !== contentId),
    };
  }

  static getColor(scData: ShareCard, scDefData: ShareCardDefinition, colors: ShareCardColor[]) {
    const defaultColor: RGBA = { r: 0, g: 0, b: 0, a: 1 };
    if (!scDefData) { return defaultColor; }

    const customColorId = scData.color_id;
    const allowCustomizeColor = !scDefData.color;

    if (!allowCustomizeColor) {
      return scDefData.color.rgba;
    } else if (customColorId) {
      return colors.find(c => c.id === customColorId).color;
    }

    return defaultColor;
  }

  static getIncludedTrackableIds(scData: ShareCard) {
    return scData.included_trackables.map(t => t.trackable_id);
  }

  private constructor(
    data: Partial<ShareCard>,
    public definition: ShareCardDefinition,
    public groupContents: GroupContentModel[],
    private onChange: ShareCardChangeListener,
  ) {
    this.data = {
      ...deepClone(defaultShareCardData),
      ...deepClone(data),
    };

    // clear any remaining associations from old format
    this.data['included_health_entries'] = null;
  }

  init(healthEntries: HealthEntriesPerCategory) {
    this.profile.init();

    healthEntryCategories.map((category) => {
      const entries = healthEntries[category.kind];
      const group = this.createHealthEntryGroup(
        category.kind,
        entries,
        category.includedValues,
        category.subType,
        category.latestItemsOnly,
      );
      group.init();
    });
  }

  // private to be uncommented when all components will use ShareCardModel via change listeners
  // mechanism instead of throwing updateCard deep in the component hierarchy along with shareCardModel
  /* private */ data: ShareCard;

  get id(): number {
    return this.data.id;
  }

  shallowCopy(onChange = this.onChange): ShareCardModel {
    return new ShareCardModel(this.data, this.definition, this.groupContents, onChange);
  }

  public groupContent = {
    isIncluded: (id) => {
      return _.includes(this.data.included_group_content, id);
    },
    add: (id) => {
      this.data.included_group_content.push(id);
    },
    remove: (id) => {
      this.data.included_group_content = _.reject(this.data.included_group_content, (h) => h === id);
    },
    toggle: (id) => {
      const exists = this.groupContent.isIncluded(id);
      return exists ? this.groupContent.remove(id) : this.groupContent.add(id);
    },
  };

  private healthEntry = {
    isIncluded: (id, valueType: ShareCardIncludedTypeNames) => {
      return this.healthEntry.includedType(id) === ShareCardIncludedTypeNames.latest || this.healthEntry.includedType(id) === ShareCardIncludedTypeNames.all;
    },
    isIncludedWithinType: (id, valueTypeGrouping: ShareCardIncludedTypeNames) => {
      const includedType = this.healthEntry.includedType(id);

      switch (valueTypeGrouping) {
        case ShareCardIncludedTypeNames.all:
          return includedType === ShareCardIncludedTypeNames.all;
        case ShareCardIncludedTypeNames.latest:
          return includedType === ShareCardIncludedTypeNames.latest || includedType === ShareCardIncludedTypeNames.all;
        default:
          return false;
      }
    },
    includedType: (id) => {
      const included = _.find(this.data.included_health_entry_values, { health_entry_id: id });
      return included ? included.included_values : undefined;
    },
    add: (id, valueType: ShareCardIncludedTypeNames = ShareCardIncludedTypeNames.latest) => {
      this.data.included_health_entry_values.push({
        health_entry_id: id,
        included_values: valueType,
      });
    },
    remove: (id, valueType: ShareCardIncludedTypeNames = ShareCardIncludedTypeNames.latest) => {
      const newHealthEntries = _.reject(this.data.included_health_entry_values, (h) => (h.health_entry_id === id && h.included_values === valueType));
      this.data.included_health_entry_values = newHealthEntries;
    },
    toggle: (id, valueType: ShareCardIncludedTypeNames = ShareCardIncludedTypeNames.latest) => {
      const exists = this.healthEntry.isIncluded(id, valueType);
      return exists ?
        this.healthEntry.remove(id, valueType) :
        this.healthEntry.add(id, valueType);
    },
  };

  public file = {
    isIncluded: (id) => {
      return _.includes(this.data.included_files, id);
    },
    add: (id) => {
      this.data.included_files.push(id);
    },
    remove: (id) => {
      this.data.included_files = _.reject(this.data.included_files, (h) => h === id);
    },
    toggle: (id) => {
      const exists = this.file.isIncluded(id);
      return exists ? this.file.remove(id) : this.file.add(id);
    },
  };

  public note = {
    isIncluded: (id) => {
      return _.includes(this.data.included_notes, id);
    },
    add: (id) => {
      this.data.included_notes.push(id);
    },
    remove: (id) => {
      this.data.included_notes = _.reject(this.data.included_notes, (h) => h === id);
    },
    toggle: (id) => {
      const exists = this.note.isIncluded(id);
      return exists ? this.note.remove(id) : this.note.add(id);
    },
  };

  public contact = {
    isIncluded: (id) => {
      return _.includes(this.data.included_contacts, id);
    },
    add: (id) => {
      this.data.included_contacts.push(id);
    },
    remove: (id) => {
      this.data.included_contacts = _.reject(this.data.included_contacts, (h) => h === id);
    },
    toggle: (id) => {
      const exists = this.contact.isIncluded(id);
      return exists ? this.contact.remove(id) : this.contact.add(id);
    },
  };

  public jewelry = {
    remove: (id) => {
      this.data.share_card_jewelry = _.reject(this.data.share_card_jewelry, (j) => j.id === id);
    },

    collection: () => {
      return this.data.share_card_jewelry.sort((a, b) => a.value.localeCompare(b.value));
    },

    isAllowed: () => {
      return this.definition.allow_medical_jewelry;
    },
  };

  createGroupContentItem(id: number) {
    return new ShareCardItem(
      () => this.groupContent.isIncluded(id),
      (included) => included ? this.groupContent.add(id) : this.groupContent.remove(id),
      () => this.definition.backpack.groupContent,
      this.triggerChange,
    );
  }

  createHealthEntryItem(category: HealthEntryCategoryNames, id: number, valueType: ShareCardIncludedTypeNames, subType: string): ShareCardItem {
    return new ShareCardItem(
      () => this.healthEntry.isIncluded(id, valueType),
      (included) => included ? this.healthEntry.add(id, valueType) : this.healthEntry.remove(id, valueType),
      () => this.getCategoryAttribute(category, valueType, subType),
      this.triggerChange,
    );
  }

  createFileItem(id: number) {
    return new ShareCardItem(
      () => this.file.isIncluded(id),
      (included) => included ? this.file.add(id) : this.file.remove(id),
      () => this.definition.backpack.files,
      this.triggerChange,
    );
  }

  createNoteItem(id: number) {
    return new ShareCardItem(
      () => this.note.isIncluded(id),
      (included) => included ? this.note.add(id) : this.note.remove(id),
      () => this.definition.backpack.notes,
      this.triggerChange,
    );
  }

  createContactItem(id: number) {
    return new ShareCardItem(
      () => this.contact.isIncluded(id),
      (included) => included ? this.contact.add(id) : this.contact.remove(id),
      () => this.definition.backpack.contacts,
      this.triggerChange,
    );
  }

  getCategoryAttribute(category: HealthEntryCategoryNames, valueType: ShareCardIncludedTypeNames, subType: string): AttributeState {
    const attributesPerCategory = {
      [HealthEntryCategoryNames.allergy]: {
        perSubType: {
          [AllergySubType.medication]: this.definition.health_entry.allergies_medication,
          [AllergySubType.food]: this.definition.health_entry.allergies_food,
          [AllergySubType.other]: this.definition.health_entry.allergies_other,
        },
      },
      [HealthEntryCategoryNames.condition]: this.definition.health_entry.conditions,
      [HealthEntryCategoryNames.medication]: this.definition.health_entry.medications,
      [HealthEntryCategoryNames.otherTreatment]: this.definition.health_entry.treatments,
      [HealthEntryCategoryNames.procedure]: this.definition.health_entry.procedures,
      [HealthEntryCategoryNames.vaccination]: this.definition.health_entry.vaccinations,
      [HealthEntryCategoryNames.lab]: this.definition.health_entry.labs,
      [HealthEntryCategoryNames.measurement]: this.definition.health_entry.measurements,
      [HealthEntryCategoryNames.geneticResult]: this.definition.health_entry.genetic_results,
    };
    const attribute = attributesPerCategory[category];

    return attribute['perSubType'] ? attribute['perSubType'][subType] : attribute;
  }

  createGroupContentsGroup() {
    const groupContentAttributes = this.definition.backpack.groupContent;
    const items = this.groupContents.map((content: GroupContentModel) => this.createGroupContentItem(content.data.id));

    return new ShareCardItemGroup(items, groupContentAttributes);
  }

  createHealthEntryGroup(
    category: HealthEntryCategoryNames,
    healthEntries: HealthEntries,
    valueType: ShareCardIncludedTypeNames,
    subType: AllergySubType,
    latestItemsOnly: boolean,
  ): ShareCardItemGroup {
    const filtered = filterHealthEntriesBySubType(healthEntries, subType);
    const groupEntries = latestItemsOnly ? filtered.newer : filtered.all;

    const items = groupEntries.map(he => this.createHealthEntryItem(category, he.id, valueType, subType));
    return new ShareCardItemGroup(items, this.getCategoryAttribute(category, valueType, subType));
  }

  createFileGroup(files): ShareCardItemGroup {
    const fileAttributes = this.definition.backpack.files;
    const items = files.map(f => this.createFileItem(f.id));

    return new ShareCardItemGroup(items, fileAttributes);
  }

  createNoteGroup(notes): ShareCardItemGroup {
    const noteAttributes = this.definition.backpack.notes;
    const items = notes.map(n => this.createNoteItem(n.id));

    return new ShareCardItemGroup(items, noteAttributes);
  }

  createContactGroup(contacts: ContactModelType[]): ShareCardItemGroup {
    const contactAttributes = this.definition.backpack.contacts;
    const items = contacts.map(f => this.createContactItem(f.id));

    return new ShareCardItemGroup(items, contactAttributes);
  }

  private triggerChange = () => {
    this.onChange(this);
  }

  profileName = new ShareCardItem(
    () => this.data.include_name,
    (include) => this.data.include_name = include,
    () => this.definition.profile.name,
    this.triggerChange,
  );

  profileAge = new ShareCardItem(
    () => this.data.include_date_of_birth,
    (include) => this.data.include_date_of_birth = include,
    () => this.definition.profile.date_of_birth,
    this.triggerChange,
  );

  profileGender = new ShareCardItem(
    () => this.data.include_gender,
    (include) => this.data.include_gender = include,
    () => this.definition.profile.gender,
    this.triggerChange,
  );

  profileBirthSex = new ShareCardItem(
    () => this.data.include_birth_sex,
    (include) => this.data.include_birth_sex = include,
    () => this.definition.profile.birth_sex,
    this.triggerChange,
  );

  profileResidence = new ShareCardItem(
    () => this.data.include_residence,
    (include) => this.data.include_residence = include,
    () => this.definition.profile.residence,
    this.triggerChange,
  );

  profileBloodType = new ShareCardItem(
    () => this.data.include_blood_type,
    (include) => this.data.include_blood_type = include,
    () => this.definition.profile.blood_type,
    this.triggerChange,
  );

  profileRace = new ShareCardItem(
    () => this.data.include_race,
    (include) => this.data.include_race = include,
    () => this.definition.profile.race,
    this.triggerChange,
  );

  profilePhoto = new ShareCardItem(
    () => this.data.include_photo,
    (include) => this.data.include_photo = include,
    () => this.definition.profile.photo,
    this.triggerChange,
  );

  profile = new ShareCardItemGroup(
    [
      this.profileName, this.profileAge, this.profileGender, this.profileBirthSex, this.profilePhoto,
      this.profileBloodType, this.profileRace, this.profileResidence,
    ],
    AttributeState.optional,
  );

  shareCardURL = new ShareCardItem(
    () => this.data.url_accessible,
    (include) => this.data.url_accessible = include,
    () => this.definition.share_card.url,
    this.triggerChange,
  );

  get customTitle() {
    return this.data.title;
  }

  get lightIcon() {
    return this.definition.icon && this.definition.icon.url_light;
  }

  get darkIcon() {
    return this.definition.icon && this.definition.icon.url_dark;
  }

  get iconClass() {
    if (!this.definition.icon) return null;

    return this.definition.icon.type;
  }

  title = {
    // empty string check instead of just !! to allow setting title to default value while editing
    get: (languageCode) => !this.definition.title_editable || (this.data.title !== '' && !this.data.title)
      ? getLocalText(this.definition.title, languageCode)
      : this.data.title,
    set: (value) => { this.data.title = value; },
  };

  canEditTitle() {
    return this.definition.title_editable;
  }

  canEditComment() {
    return this.definition.share_card.comments !== AttributeState.excluded;
  }

  canEditURL() {
    return this.definition.share_card.url !== AttributeState.excluded;
  }

  canDelete() {
    return this.definition.user_deletable;
  }

  isGroupAccessible() {
    return this.definition.group_accessible;
  }

  color = {
    get: () => this.data.color_id,
    set: (value) => { this.data.color_id = value; },
  };

  canChangeColor(): boolean {
    return !this.definition.color;
  }

  get color_id() {
    return this.data.color_id;
  }

  set color_id(colorId) {
    this.data.color_id = colorId;
  }

  getColor(shareCardColors: ShareCardColor[]): RGBA {
    if (!this.canChangeColor()) {
      return this.definition.color.rgba;
    }

    if (this.color_id) {
      const customColor = shareCardColors.find((c) => c.id === this.color_id);

      if (customColor) {
        return customColor.color;
      }
    }

    const defaultColor: RGBA = { r: 0, g: 0, b: 0, a: 1 };
    return defaultColor;
  }

  comment = {
    hasComment: () => !!this.data.comment,
    get: () => this.data.comment,
    set: (value) => { this.data.comment = value; },
  };

  get passcode() {
    return this.data.access_code;
  }

  set passcode(value) {
    this.data.access_code = value;
  }

  get birthDatePrecision() {
    return this.definition.date_of_birth_precision;
  }

  get definitionUid() {
    return this.definition.uid;
  }

  get isUrlAccessible() {
    return this.data.url_accessible;
  }

  get updatedAt() {
    return this.data.updated_at;
  }

  get lastAccessedAt() {
    return this.data.last_accessed_at;
  }

  get isGroupContentAllowed() {
    return this.definition.group_content_allowed && !!this.groupContents.length;
  }

  countFiles() {
    return this.data.included_files.length;
  }

  countNotes() {
    return this.data.included_notes.length;
  }

  countContacts() {
    return this.data.included_contacts.length;
  }

  countHealthEntries() {
    return this.data.included_health_entry_values.length;
  }
}

export function filterHealthEntriesBySubType(healthEntries: HealthEntries, subType: string): HealthEntries {
  if (!subType) return healthEntries;

  const filter = entry => entry.subType && entry.subType.get() === subType;

  return {
    all: healthEntries.all.filter(filter),
    newer: healthEntries.newer.filter(filter),
    older: healthEntries.older.filter(filter),
  };
}

function deepClone(obj) {
  if (!obj) return obj;
  return JSON.parse(JSON.stringify(obj));
}


export default ShareCardModel;
